import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useImageUrl } from '../api/useImageUrl';
import { CameraPicker, addSafetyCamera } from '../components/liveCameras';
import { SafetyScenarioWizard } from './SafetyScenarioWizard';

/**
 * Workplace safety — the native Visionify-style monitor.
 *
 * A frame in (an upload or a webcam grab while camera sources are still being
 * decided), and back come the people, PPE, fire and vehicles the detectors
 * found, plus the events worth acting on. The page is honest about how each
 * answer was reached: a trained model, or a colour heuristic that flags rather
 * than proves. That label rides on every result, because a safety screen is the
 * last place a guess should look like a fact.
 */

interface PpE {
  vest?: string;
  helmet?: string;
  basis?: string;
}
interface Detection {
  kind: string;
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
  meta?: { ppe?: PpE; basis?: string };
}
interface DetectResult {
  imageKey: string;
  overlayKey: string | null;
  detectors: Record<string, string>;
  detections: Detection[];
  counts: { persons: number; missingPpe: number; fires: number; forklifts: number };
  events: Array<{ kind: string; severity: string; detail: string }>;
  latencyMs: number;
}
interface SafetyEvent {
  id: string;
  time: string;
  kind: string;
  severity: string;
  detail: string;
  overlayKey: string | null;
  acknowledged: boolean;
}

const SEV_COLOUR: Record<string, string> = {
  critical: 'var(--bad)',
  warning: 'var(--warn)',
  info: 'var(--teal)',
};
const KIND_LABEL: Record<string, string> = {
  ppe_violation: 'PPE violation',
  fire: 'Fire / smoke',
  near_miss: 'Near miss',
  intrusion: 'Zone intrusion',
  occupancy: 'Occupancy',
};
const DETECTOR_OPTS: Array<{ key: string; label: string; note?: string }> = [
  { key: 'person', label: 'People / vehicles', note: 'trained model' },
  { key: 'ppe', label: 'PPE (vest / helmet)', note: 'trained model' },
  { key: 'fire', label: 'Fire / smoke', note: 'colour guess — off by default' },
  { key: 'forklift', label: 'Forklift', note: 'no model yet' },
];

interface VideoResult {
  overlayKey: string | null;
  detectors: Record<string, string>;
  frames: number;
  timeline: Array<{ t: number; persons: number; fires: number; missingPpe: number }>;
  peak: { t: number; persons: number; fires: number; missingPpe: number };
  events: Array<{ kind: string; severity: string; detail: string }>;
  latencyMs: number;
}

interface CameraOpt {
  id: number;
  name: string;
  source: string;
  customerName?: string | null;
  zoneCount: number;
}

export function SafetyView() {
  const [result, setResult] = useState<DetectResult | null>(null);
  const [videoResult, setVideoResult] = useState<VideoResult | null>(null);
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [summary, setSummary] = useState<{ total: number; unacked: number } | null>(null);
  const [cameras, setCameras] = useState<CameraOpt[]>([]);
  const [cameraId, setCameraId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wizard, setWizard] = useState(false);
  // Which detectors run on an upload/grab. person + ppe are trained models, on by
  // default. fire is a colour heuristic that cries wolf on orange machines/hi-vis,
  // and forklift has no model yet — both off by default, opt in when you want them.
  const [kinds, setKinds] = useState<Record<string, boolean>>({
    person: true,
    ppe: true,
    fire: false,
    forklift: false,
  });
  const toggleKind = useCallback(
    (k: string) => setKinds((prev) => ({ ...prev, [k]: !prev[k] })),
    [],
  );
  const kindsCsv = useMemo(
    () => Object.entries(kinds).filter(([, on]) => on).map(([k]) => k).join(','),
    [kinds],
  );

  const loadCameras = useCallback(() => {
    fetch('/api/safety/cameras')
      .then((r) => (r.ok ? r.json() : []))
      .then(setCameras)
      .catch(() => undefined);
  }, []);
  useEffect(() => loadCameras(), [loadCameras]);
  const addCamera = async () => {
    try {
      const id = await addSafetyCamera();
      if (id == null) return;
      loadCameras();
      setCameraId(id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const load = useCallback(() => {
    fetch('/api/safety/events?limit=25')
      .then((r) => (r.ok ? r.json() : []))
      .then(setEvents)
      .catch(() => undefined);
    fetch('/api/safety/summary')
      .then((r) => (r.ok ? r.json() : null))
      .then(setSummary)
      .catch(() => undefined);
  }, []);
  useEffect(() => load(), [load]);

  const submit = useCallback(
    async (blob: Blob, filename: string) => {
      setBusy(true);
      setError(null);
      try {
        const form = new FormData();
        form.append('image', blob, filename);
        form.append('kinds', kindsCsv || 'person');
        if (cameraId) form.append('cameraId', String(cameraId));
        const res = await fetch('/api/safety/detect', { method: 'POST', body: form });
        if (!res.ok) throw new Error(((await res.json()) as { message?: string }).message ?? 'Detection failed');
        setVideoResult(null);
        setResult(await res.json());
        load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [load, cameraId, kindsCsv],
  );

  const submitVideo = useCallback(
    async (file: File) => {
      setBusy(true);
      setError(null);
      try {
        const form = new FormData();
        form.append('video', file, file.name);
        form.append('kinds', kindsCsv || 'person');
        if (cameraId) form.append('cameraId', String(cameraId));
        const res = await fetch('/api/safety/detect-video', { method: 'POST', body: form });
        if (!res.ok) throw new Error(((await res.json()) as { message?: string }).message ?? 'Detection failed');
        setResult(null);
        setVideoResult(await res.json());
        load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [load, cameraId, kindsCsv],
  );

  const onFile = useCallback(
    (f: File) => (f.type.startsWith('video/') ? void submitVideo(f) : void submit(f, f.name)),
    [submit, submitVideo],
  );

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h1 className="view-title">Safety monitoring</h1>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <button className="btn sm primary" onClick={() => setWizard(true)}>+ New scenario</button>
          <Link to="/safety/dashboard" style={{ fontSize: 13 }}>Dashboard</Link>
          <Link to="/safety/events" style={{ fontSize: 13 }}>Events</Link>
          <Link to="/safety/zones" style={{ fontSize: 13 }}>Cameras &amp; zones</Link>
        </div>
      </div>

      {wizard && (
        <SafetyScenarioWizard
          onClose={() => setWizard(false)}
          onDone={() => { setWizard(false); loadCameras(); }}
        />
      )}
      <p className="muted" style={{ marginTop: -8, maxWidth: 720 }}>
        Turn a camera frame into safety events — people without PPE, flames, a person in a vehicle's
        path. Upload or grab a frame to test it now; RTSP camera feeds come next.
      </p>

      {summary && (
        <div className="row" style={{ marginBottom: 16 }}>
          <StatBadge label="Events · 24h" value={summary.total} tone="var(--accent)" />
          <StatBadge label="Open" value={summary.unacked} tone={summary.unacked > 0 ? 'var(--bad)' : 'var(--teal)'} />
          <StatBadge label="This frame · people" value={result?.counts.persons ?? '—'} tone="var(--teal)" />
          <StatBadge label="Missing PPE" value={result?.counts.missingPpe ?? '—'} tone="var(--warn)" />
          <StatBadge label="Fire regions" value={result?.counts.fires ?? '—'} tone="var(--bad)" />
        </div>
      )}

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1.3fr 1fr', gap: 20 }}>
        <div className="grid" style={{ alignContent: 'start' }}>
          {videoResult ? (
            <VideoResultPanel result={videoResult} />
          ) : result ? (
            <ResultPanel result={result} />
          ) : (
            <div className="panel muted">
              Nothing checked yet. Upload an image or a video clip, or grab a frame on the right.
            </div>
          )}
          <EventsLog events={events} />
        </div>

        <div className="grid" style={{ alignContent: 'start' }}>
          <div className="panel">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <strong style={{ fontSize: 13 }}>Camera</strong>
              <Link to="/safety/zones" style={{ fontSize: 12 }}>Cameras &amp; zones →</Link>
            </div>
            <div style={{ marginTop: 8 }}>
              <CameraPicker
                cameras={cameras}
                value={cameraId === '' ? null : cameraId}
                onChange={(id) => setCameraId(id ?? '')}
                onAdd={() => void addCamera()}
                placeholder="Ad-hoc (no camera — global rules)"
                style={{ width: '100%' }}
              />
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              Pick a camera to evaluate its zones (PPE-required, no-entry, occupancy). Ad-hoc runs
              the global checks.
            </div>
          </div>
          <div className="panel">
            <strong style={{ fontSize: 13 }}>Detectors to run</strong>
            <div className="grid" style={{ gap: 6, marginTop: 8 }}>
              {DETECTOR_OPTS.map((d) => (
                <label key={d.key} className="row" style={{ gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={kinds[d.key] ?? false} onChange={() => toggleKind(d.key)} />
                  <span>{d.label}</span>
                  {d.note && <span className="muted" style={{ fontSize: 11 }}>— {d.note}</span>}
                </label>
              ))}
            </div>
            {kinds.fire && (
              <div className="muted" style={{ fontSize: 11, marginTop: 8, color: 'var(--warn)' }}>
                Fire is a colour heuristic — it flags orange/red regions, so orange machines or hi-vis
                can trigger a false alarm.
              </div>
            )}
          </div>
          <DropZone onFile={onFile} busy={busy} />
          <WebcamCapture onCapture={(b) => submit(b, 'webcam.jpg')} busy={busy} />
          <div className="panel">
            <strong style={{ fontSize: 13 }}>How each thing is detected</strong>
            <ul className="muted" style={{ fontSize: 12, lineHeight: 1.6, margin: '8px 0 0', paddingLeft: 18 }}>
              <li><strong>People / vehicles</strong> — a trained model (YOLOX, Apache-2.0).</li>
              <li><strong>PPE (vest / helmet)</strong> — a trained model per person when one is installed (verdict basis reads <code>ppe-model</code>), otherwise a hi-vis colour heuristic. "unknown" means the model saw neither the item nor its explicit absence on that person — often a frame unlike its training data.</li>
              <li><strong>Fire / smoke</strong> — a flame-colour heuristic. Catches the obvious, misses pale smoke.</li>
              <li><strong>Forklift</strong> — a coarse vehicle proxy until a forklift-specific model is added.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Helmet / vest verdict for one person: ✓ yes, ✕ no, ? unknown. */
function PpeChip({ label, value }: { label: string; value?: string }) {
  const v = value ?? 'unknown';
  const style: Record<string, { icon: string; colour: string }> = {
    yes: { icon: '✓', colour: 'var(--good)' },
    no: { icon: '✕', colour: 'var(--bad)' },
  };
  const s = style[v] ?? { icon: '?', colour: 'var(--warn)' };
  return (
    <span style={{ fontSize: 13 }}>
      {label}: <strong style={{ color: s.colour }}>{s.icon} {v}</strong>
    </span>
  );
}

function ResultPanel({ result }: { result: DetectResult }) {
  const overlayUrl = useImageUrl(result.overlayKey ?? '');
  const persons = result.detections.filter((d) => d.kind === 'person');
  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <strong>What the frame showed</strong>
        <span className="muted" style={{ fontSize: 12 }}>{result.latencyMs} ms</span>
      </div>
      {overlayUrl && (
        <img
          src={overlayUrl}
          alt="detections"
          style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)', display: 'block' }}
        />
      )}

      {/* The PPE verdict per person — shown always, not only when it becomes a
          violation, so "why" is visible even when everything is fine or the model
          was unsure. "unknown" means the model saw neither the item nor its
          explicit absence on that person (often an out-of-domain frame). */}
      {persons.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
            Per person — PPE
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {persons.map((p, i) => (
              <div key={i} className="row" style={{ gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 12 }}>
                  Person {i + 1} ({Math.round(p.confidence * 100)}%)
                </span>
                <PpeChip label="helmet" value={p.meta?.ppe?.helmet} />
                <PpeChip label="vest" value={p.meta?.ppe?.vest} />
                {(p.meta?.ppe?.basis ?? p.meta?.basis) && (
                  <span className="muted" style={{ fontSize: 11 }}>· {p.meta?.ppe?.basis ?? p.meta?.basis}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {result.events.length > 0 ? (
        <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
          {result.events.map((e, i) => (
            <div
              key={i}
              style={{
                borderLeft: `3px solid ${SEV_COLOUR[e.severity] ?? 'var(--border)'}`,
                paddingLeft: 8,
                fontSize: 13,
              }}
            >
              <strong style={{ color: SEV_COLOUR[e.severity] }}>
                {KIND_LABEL[e.kind] ?? e.kind}
              </strong>{' '}
              <span className="muted">— {e.detail}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 13, marginTop: 12 }}>
          {result.detections.length} detection{result.detections.length === 1 ? '' : 's'}, nothing
          that warranted an event.
        </div>
      )}
    </div>
  );
}

function EventsLog({ events }: { events: SafetyEvent[] }) {
  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>Recent events</strong>
        <Link to="/safety/events" style={{ fontSize: 12 }}>All events &amp; triage →</Link>
      </div>
      {events.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>Nothing logged yet.</div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 8 }}>
          <table>
            <thead>
              <tr className="muted" style={{ fontSize: 12 }}>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Time</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Event</th>
                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="muted" style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontSize: 12 }}>
                    {new Date(e.time).toLocaleString('nl-NL')}
                  </td>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                    <span style={{ color: SEV_COLOUR[e.severity], fontWeight: 600, fontSize: 13 }}>
                      {KIND_LABEL[e.kind] ?? e.kind}
                    </span>
                  </td>
                  <td className="muted" style={{ padding: '6px 8px', fontSize: 12 }}>{e.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatBadge({ label, value, tone }: { label: string; value: string | number; tone: string }) {
  return (
    <div
      className="stat-tile"
      style={{ flex: '1 1 150px', ['--tone' as string]: tone } as React.CSSProperties}
    >
      <div className="stat-value" style={{ fontSize: 28 }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

function DropZone({ onFile, busy }: { onFile: (f: File) => void; busy: boolean }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="panel">
      <strong>Upload a frame or clip</strong>
      <div
        className={`dropzone ${drag ? 'drag' : ''}`}
        style={{ marginTop: 12 }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files[0];
          if (f) onFile(f);
        }}
      >
        {busy ? 'Detecting…' : 'Drop an image or video here, or click to browse'}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

function VideoResultPanel({ result }: { result: VideoResult }) {
  const overlayUrl = useImageUrl(result.overlayKey ?? '');
  const maxP = Math.max(1, ...result.timeline.map((f) => f.persons));
  return (
    <div className="panel">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <strong>Clip reviewed — {result.frames} frames sampled</strong>
        <span className="muted" style={{ fontSize: 12 }}>{result.latencyMs} ms</span>
      </div>
      {overlayUrl && (
        <>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Busiest frame (t = {result.peak.t}s): {result.peak.persons} people
            {result.peak.missingPpe > 0 ? `, ${result.peak.missingPpe} missing PPE` : ''}
            {result.peak.fires > 0 ? `, ${result.peak.fires} fire` : ''}
          </div>
          <img
            src={overlayUrl}
            alt="busiest frame"
            style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border)', display: 'block' }}
          />
        </>
      )}
      {/* People over time — a plain bar per sampled frame, warm where PPE is missing. */}
      <div style={{ marginTop: 12 }}>
        <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>People over time</div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60 }}>
          {result.timeline.map((f, i) => (
            <div
              key={i}
              title={`t=${f.t}s · ${f.persons} people${f.missingPpe ? ` · ${f.missingPpe} missing PPE` : ''}${f.fires ? ` · fire` : ''}`}
              style={{
                flex: 1,
                height: `${(f.persons / maxP) * 100}%`,
                minHeight: f.persons > 0 ? 3 : 1,
                background: f.fires > 0 ? 'var(--bad)' : f.missingPpe > 0 ? 'var(--warn)' : 'var(--teal)',
                borderRadius: 2,
                opacity: f.persons > 0 || f.fires > 0 ? 1 : 0.25,
              }}
            />
          ))}
        </div>
      </div>
      {result.events.length > 0 && (
        <div style={{ marginTop: 12, display: 'grid', gap: 6 }}>
          {result.events.map((e, i) => (
            <div key={i} style={{ borderLeft: `3px solid ${SEV_COLOUR[e.severity] ?? 'var(--border)'}`, paddingLeft: 8, fontSize: 13 }}>
              <strong style={{ color: SEV_COLOUR[e.severity] }}>{KIND_LABEL[e.kind] ?? e.kind}</strong>{' '}
              <span className="muted">— {e.detail}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Grab from a camera connected directly to this PC — built-in or USB.
 *
 * A local device is reachable only from the browser on the machine it is plugged
 * into (getUserMedia), never from the server, so this is where a USB safety feed
 * is used: the operator opens this on the facility PC, picks the camera, and
 * captures. A machine often has more than one (a laptop lid camera and the real
 * safety camera on USB), so which one is a choice, not a guess — device labels
 * only appear once camera permission is granted, so the list fills in after Enable.
 */
function WebcamCapture({ onCapture, busy }: { onCapture: (b: Blob) => void; busy: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [on, setOn] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);
  useEffect(() => stop, [stop]);

  const listDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter((d) => d.kind === 'videoinput');
      setDevices(cams);
      setDeviceId((cur) => cur || cams[0]?.deviceId || '');
    } catch {
      /* enumeration blocked — the plain default camera still works */
    }
  }, []);

  // A first, label-less list before permission, so the control is at least there.
  useEffect(() => {
    void listDevices();
  }, [listDevices]);

  const start = useCallback(
    async (id?: string) => {
      setErr(null);
      stop();
      try {
        const pick = id ?? deviceId;
        const stream = await navigator.mediaDevices.getUserMedia({
          video: pick ? { deviceId: { exact: pick } } : true,
        });
        streamRef.current = stream;
        setOn(true);
        // Labels populate only after permission — re-list to name the cameras.
        await listDevices();
      } catch (e) {
        setErr((e as Error).message);
      }
    },
    [deviceId, stop, listDevices],
  );

  useEffect(() => {
    const v = videoRef.current;
    if (on && v && streamRef.current) {
      v.srcObject = streamRef.current;
      void v.play().catch(() => undefined);
    }
  }, [on]);

  const switchDevice = async (id: string) => {
    setDeviceId(id);
    if (on) await start(id);
  };

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')?.drawImage(v, 0, 0);
    c.toBlob((b) => b && onCapture(b), 'image/jpeg', 0.92);
  };

  return (
    <div className="panel">
      <strong>Grab from a connected camera</strong>
      <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
        A camera plugged into this PC — built-in or USB.
      </p>
      {devices.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <label>Camera</label>
          <select value={deviceId} onChange={(e) => void switchDevice(e.target.value)}>
            {devices.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || `Camera ${i + 1}${d.deviceId ? '' : ' (enable to name)'}`}
              </option>
            ))}
          </select>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        {!on ? (
          <button className="btn" onClick={() => void start()}>
            Enable camera
          </button>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', borderRadius: 8, background: '#000', display: 'block' }}
            />
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn primary" onClick={capture} disabled={busy}>
                {busy ? 'Detecting…' : 'Capture & check'}
              </button>
              <button className="btn sm" onClick={() => { stop(); setOn(false); }}>
                Stop
              </button>
            </div>
          </>
        )}
        {err && <div style={{ color: 'var(--bad)', marginTop: 8 }}>{err}</div>}
      </div>
    </div>
  );
}
